import { BemHelper } from "@/utils/BemHelper";
import { useActiveBreakpoints } from "@/utils/useActiveBreakpoints";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import CtPublicContainer from "../public-container/CtPublicContainer";
import { ThemeFontVariant } from "../types";
import { THEME_FONT_VARIANTS } from "../constants";
import CtButton from "../button/CtButton";
import { FaTimes } from "react-icons/fa";
import { LogoShortColorIcon } from "@/icons/color-icons";
import { BurgerIcon } from "@/icons/primary";

export type CtNavbarProps = {
  studioName: string;
  options?: React.ReactNode;
  actions?: React.ReactNode;
  sticky?: boolean;
  fontVariant?: ThemeFontVariant;
  onClick?: () => void;
  transparent?: boolean;
  dark?: boolean;
};

const bem = new BemHelper("ct-navbar");

function CtNavbar({
  studioName,
  options,
  actions,
  sticky,
  fontVariant,
  onClick,
  transparent,
  dark,
}: CtNavbarProps) {
  const [stickyScrolled, setStickyScrolled] = React.useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const { activeBreakpoints } = useActiveBreakpoints();

  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  useEffect(() => {
    if (!sticky || !window) return;

    const handleScroll = () => {
      if (window.scrollY > 0) {
        setStickyScrolled(true);
      } else {
        setStickyScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {!activeBreakpoints.md && showMenu && (
        <div className={classNames(bem.element("menu"))}>
          <div>
            <CtButton
              intent='default'
              outlined
              size='small'
              disablePadding
              className={classNames(bem.element("menu-button"))}
              onClick={handleMenuClick}
            >
              <FaTimes />
            </CtButton>
          </div>
          <ul className={classNames(bem.element("menu-options"))}>{options}</ul>
        </div>
      )}
      <nav
        className={classNames(
          bem.block(),
          sticky && bem.modifier("sticky"),
          sticky && stickyScrolled && bem.modifier("scrolled"),
          transparent && bem.modifier("transparent"),
          bem.modifier(fontVariant || THEME_FONT_VARIANTS.FONT_VARIANT_1),
          !activeBreakpoints.md && bem.modifier("mobile"),
        )}
      >
        <CtPublicContainer
          className={classNames(
            bem.element("container"),
            !activeBreakpoints.md && bem.modifier("mobile", "container"),
          )}
        >
          <div
            className={classNames(
              bem.element("left"),
              onClick && bem.modifier("has-actions", "left"),
            )}
          >
            {!activeBreakpoints.md && (
              <div className='cursor-pointer flex' onClick={handleMenuClick}>
                <BurgerIcon style={{ color: dark ? "white" : "black" }} />
              </div>
            )}
            <div
              className={classNames(
                bem.element("logo"),
                !activeBreakpoints.md && bem.modifier("mobile", "logo"),
              )}
              onClick={onClick}
            >
              <LogoShortColorIcon />
            </div>
            <div
              className={classNames(
                // bem.element("studio-name"),
                "text-4xl font-sans-serif font-extrabold",
                dark && bem.modifier("dark", "studio-name"),
                !activeBreakpoints.md && bem.modifier("mobile", "studio-name"),
                "bg-clip-text text-transparent bg-gradient-bright",
              )}
              onClick={onClick}
            >
              {studioName}
            </div>
          </div>
          <div className={classNames(bem.element("right"))}>
            <ul
              className={classNames(
                bem.element("options"),
                !activeBreakpoints.md && bem.modifier("mobile", "options"),
              )}
            >
              {options}
            </ul>
            <div className={classNames(bem.element("actions"))}>{actions}</div>
          </div>
        </CtPublicContainer>
      </nav>
      {/* add empty element to avoid cover the first element if navbar is sticky */}
      {sticky && !transparent && (
        <div
          className={classNames(
            bem.element("sticky-empty"),
            !activeBreakpoints.md && bem.modifier("mobile", "sticky-empty"),
          )}
        />
      )}
    </>
  );
}

CtNavbar.defaultProps = {
  sticky: false,
  options: null,
  actions: null,
  fontVariant: THEME_FONT_VARIANTS.FONT_VARIANT_1,
  onClick: () => {},
  transparent: false,
};

export default CtNavbar;
